import { useCallback, useEffect, useRef, useState } from 'react';
import { TruckRecordingMipFilterDTO } from '../../../api/generated';
import { TruckRecordingMip, useTruckRecordingMipList } from '../../../api/queries/truckRecordingMipList';
import { ResourceStatus } from '../../../api/resources/useOcInsightsEvents';

export interface MipActivityRecording {
  id: string;
  displayedCaptureStartAt: string;
  mipReportId: string;
  licensePlate?: string;
  imageStatus: ResourceStatus;
  materialStatus: ResourceStatus;
  soilStatus: ResourceStatus;
  volumeStatus: ResourceStatus;
  edgeStatus: ResourceStatus;
  licensePlateStatus: ResourceStatus;
}

const INITIAL_DELAY = 10 * 1000;
const POLL_INTERVAL = 3 * 60 * 1000;
const MAX_DURATION = 60 * 60 * 1000;

const FALLBACK_PLATES = ['M-AX-905', 'LAU-RA', 'LU-CA', 'LA-RS', 'CY-CL', 'DI-ESEL'];

type SimulatedStatusKey = 'imageStatus' | 'materialStatus' | 'soilStatus' | 'edgeStatus' | 'volumeStatus';

const STATUS_TIMELINE: ReadonlyArray<{ key: SimulatedStatusKey; delay: number }> = [
  { key: 'imageStatus', delay: 2000 },
  { key: 'materialStatus', delay: 4000 },
  { key: 'soilStatus', delay: 6000 },
  { key: 'edgeStatus', delay: 8000 },
  { key: 'volumeStatus', delay: 10000 },
];

interface DemoState {
  activities: MipActivityRecording[];
  count: number;
  startedAt: number;
}

const storageKey = (jobsiteId: string) => `demo-live-${jobsiteId}`;

function isSameDay(a: number, b: number): boolean {
  const da = new Date(a);
  const db = new Date(b);
  return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
}

function catchUp(activity: MipActivityRecording): MipActivityRecording {
  const capturedAt = new Date(activity.displayedCaptureStartAt).getTime();
  if (Number.isNaN(capturedAt)) return activity;

  const elapsed = Date.now() - capturedAt;
  const caughtUp = { ...activity };
  for (const { key, delay } of STATUS_TIMELINE) {
    if (caughtUp[key] === 'not-started' && elapsed >= delay) caughtUp[key] = 'finished';
  }
  return caughtUp;
}

function loadState(jobsiteId: string): DemoState | null {
  try {
    const saved = sessionStorage.getItem(storageKey(jobsiteId));
    if (!saved) return null;

    const state: DemoState = JSON.parse(saved);
    const expired = Date.now() - state.startedAt > MAX_DURATION || !isSameDay(state.startedAt, Date.now());
    if (expired) return null;

    return { ...state, activities: state.activities.map(catchUp) };
  } catch {
    return null;
  }
}

function saveState(jobsiteId: string, state: DemoState): void {
  try {
    sessionStorage.setItem(storageKey(jobsiteId), JSON.stringify(state));
  } catch {
    // sessionStorage unavailable
  }
}

export function useDemoLiveActivities(jobsiteId: string) {
  const [activities, setActivities] = useState<MipActivityRecording[]>([]);

  const poolRef = useRef<TruckRecordingMip[]>([]);
  const countRef = useRef(0);
  const startedAtRef = useRef(Date.now());
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const startedRef = useRef(false);

  // Fetched once, then cloned on a loop to fake a live feed.
  const { data: mipList } = useTruckRecordingMipList({
    jobsiteId,
    filter: {} as TruckRecordingMipFilterDTO,
    pageSize: 20,
    recordingsPage: 0,
    recordingsSorting: { sortField: 'observedAt', sortDirection: 'desc' },
  });
  const hasPool = !!mipList?.data?.length;

  const finishStatus = useCallback((id: string, key: SimulatedStatusKey) => {
    setActivities((prev) => prev.map((a) => (a.id === id ? { ...a, [key]: 'finished' } : a)));
  }, []);

  // Emit one activity cloned from the next pool recording, then schedule the next tick.
  const tick = useCallback(() => {
    const pool = poolRef.current;
    if (pool.length === 0 || Date.now() - startedAtRef.current > MAX_DURATION) return;

    const recording = pool[countRef.current % pool.length];
    const licensePlate = recording.licensePlate || FALLBACK_PLATES[countRef.current % FALLBACK_PLATES.length];
    countRef.current += 1;

    const activity: MipActivityRecording = {
      id: `live-demo-${recording.id}-${countRef.current}`,
      displayedCaptureStartAt: new Date().toISOString(),
      mipReportId: recording.id,
      licensePlate,
      imageStatus: 'not-started',
      materialStatus: 'not-started',
      soilStatus: 'not-started',
      volumeStatus: 'not-started',
      edgeStatus: 'not-started',
      licensePlateStatus: 'finished',
    };

    setActivities((prev) => [...prev, activity]);
    STATUS_TIMELINE.forEach(({ key, delay }) =>
      timersRef.current.push(setTimeout(() => finishStatus(activity.id, key), delay))
    );
    timersRef.current.push(setTimeout(tick, POLL_INTERVAL));
  }, [finishStatus]);

  // Initialise once the pool is ready (re-runs and resets when the jobsite changes).
  useEffect(() => {
    if (!jobsiteId || !hasPool) return;
    poolRef.current = mipList!.data!;
    startedRef.current = true;

    const saved = loadState(jobsiteId);
    countRef.current = saved?.count ?? 0;
    startedAtRef.current = saved?.startedAt ?? Date.now();
    setActivities(saved?.activities ?? []);

    timersRef.current.push(setTimeout(tick, INITIAL_DELAY));

    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
      startedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobsiteId, hasPool, tick]);

  useEffect(() => {
    if (!startedRef.current) return;
    saveState(jobsiteId, { activities, count: countRef.current, startedAt: startedAtRef.current });
  }, [jobsiteId, activities]);

  return { isCameraOnline: true, activities };
}
